import { useState, useRef } from 'react';
import { Upload, FileJson, Clock, CheckCircle2, XCircle, Loader2, FolderInput } from 'lucide-react';
import { useImportJobs, useStartImport, type ImportJob } from '../hooks/useImport';
import { Badge } from '../components/shared/Badge';
import { cn } from '../lib/cn';

interface ImportViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const statusColors: Record<string, string> = {
  queued: 'gray',
  running: 'blue',
  completed: 'green',
  failed: 'red',
};

const statusIcons: Record<string, typeof Clock> = {
  queued: Clock,
  running: Loader2,
  completed: CheckCircle2,
  failed: XCircle,
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function ImportView({ onNavigate }: ImportViewProps) {
  const { data: jobsData, isLoading } = useImportJobs();
  const startImport = useStartImport();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [s3Path, setS3Path] = useState('');
  const [dragOver, setDragOver] = useState(false);

  function handleFileDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    const jsonFile = files.find((f) => f.name.endsWith('.json'));
    if (jsonFile) {
      handleFile(jsonFile);
    }
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  }

  async function handleFile(file: File) {
    const text = await file.text();
    try {
      const content = JSON.parse(text);
      startImport.mutate({ source: 'file', fileContent: content });
    } catch {
      // Invalid JSON
    }
  }

  function handleS3Import(e: React.FormEvent) {
    e.preventDefault();
    if (!s3Path.trim()) return;
    startImport.mutate({ source: 's3', filePath: s3Path.trim() });
    setS3Path('');
  }

  const jobs = jobsData?.jobs ?? [];

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Import</h1>
          <p className="mt-0.5 text-sm text-slate-500">Import contacts and organizations from email data</p>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Upload Area */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* File Upload */}
          <div
            className={cn(
              'card flex flex-col items-center justify-center border-2 border-dashed p-8 transition-colors',
              dragOver ? 'border-brand-400 bg-brand-50' : 'border-surface-300',
            )}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleFileDrop}
          >
            <div className="mb-3 rounded-full bg-surface-100 p-3">
              <Upload className="h-6 w-6 text-slate-400" />
            </div>
            <h3 className="text-sm font-semibold text-slate-700">Upload JSON File</h3>
            <p className="mt-1 text-xs text-slate-400 text-center">
              Drag and drop a JSON file here, or click to browse
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="btn-secondary btn-sm mt-3"
              disabled={startImport.isPending}
            >
              <FileJson className="h-3.5 w-3.5" />
              Browse Files
            </button>
          </div>

          {/* S3 Import */}
          <div className="card p-6">
            <div className="mb-3 flex items-center gap-2">
              <FolderInput className="h-5 w-5 text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-700">S3 Bulk Import</h3>
            </div>
            <p className="mb-4 text-xs text-slate-400">
              Enter an S3 path to import multiple files at once.
            </p>
            <form onSubmit={handleS3Import} className="flex gap-2">
              <input
                type="text"
                value={s3Path}
                onChange={(e) => setS3Path(e.target.value)}
                placeholder="s3://bucket/path/to/files/"
                className="input flex-1"
              />
              <button
                type="submit"
                className="btn-primary btn-sm"
                disabled={!s3Path.trim() || startImport.isPending}
              >
                {startImport.isPending ? 'Starting...' : 'Import'}
              </button>
            </form>
          </div>
        </div>

        {/* Format Instructions */}
        <div className="card p-4">
          <h3 className="mb-2 text-sm font-semibold text-slate-700">Expected JSON Format</h3>
          <p className="text-xs text-slate-500 leading-relaxed">
            JSON files should follow the outlook-email-manager export format. Each file contains an
            array of email records with sender, recipients, subject, body, and attachment metadata.
            The importer will extract contact information, organization details, and create
            interaction records automatically.
          </p>
        </div>

        {/* Import History */}
        <div>
          <h2 className="mb-3 text-lg font-semibold text-slate-800">Import History</h2>
          {isLoading ? (
            <div className="card divide-y divide-surface-100">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-4 py-3">
                  <div className="h-5 w-5 animate-pulse rounded-full bg-surface-200" />
                  <div className="flex-1">
                    <div className="h-4 w-40 animate-pulse rounded bg-surface-200" />
                    <div className="mt-1 h-3 w-24 animate-pulse rounded bg-surface-100" />
                  </div>
                </div>
              ))}
            </div>
          ) : jobs.length > 0 ? (
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-header">Status</th>
                    <th className="table-header">Source</th>
                    <th className="table-header">Files</th>
                    <th className="table-header">Contacts</th>
                    <th className="table-header">Organizations</th>
                    <th className="table-header">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => {
                    const StatusIcon = statusIcons[job.status] ?? Clock;
                    return (
                      <tr key={job.id} className="table-row-hover">
                        <td className="table-cell">
                          <div className="flex items-center gap-1.5">
                            <StatusIcon className={cn(
                              'h-4 w-4',
                              job.status === 'running' && 'animate-spin text-blue-500',
                              job.status === 'completed' && 'text-green-500',
                              job.status === 'failed' && 'text-red-500',
                              job.status === 'queued' && 'text-slate-400',
                            )} />
                            <Badge color={statusColors[job.status] ?? 'gray'}>{job.status}</Badge>
                          </div>
                        </td>
                        <td className="table-cell text-sm text-slate-600">
                          {job.fileName ?? job.source}
                        </td>
                        <td className="table-cell text-sm text-slate-600">
                          {job.filesProcessed ?? '--'}
                        </td>
                        <td className="table-cell text-sm text-slate-600">
                          {job.contactsCreated ?? '--'}
                        </td>
                        <td className="table-cell text-sm text-slate-600">
                          {job.organizationsCreated ?? '--'}
                        </td>
                        <td className="table-cell text-xs text-slate-500">
                          {formatDate(job.createdAt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="card p-8 text-center text-sm text-slate-400">
              No imports yet. Upload a file or provide an S3 path to get started.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
